import { compileQuery, parseDateSpec } from "@engram/core/search";
import { SUBTREE_SQL } from "./notebookTree";
import type { SortKey } from "./prefs";
import { dateFmt } from "./format";

/** What the note list is showing. Mirrors the URL. */
export type ListContext =
  | { kind: "all" }
  | { kind: "notebook"; id: string }
  | { kind: "stack"; id: string }
  | { kind: "tag"; id: string }
  | { kind: "search"; q: string }
  | { kind: "trash" }
  | { kind: "shortcuts" };

export interface NoteRow {
  id: string;
  title: string;
  snippet: string;
  updated: number;
  created: number;
  notebook_id: string;
  tag_ids: string;
  pinned_at: number | null;
  locked_at: number | null;
  thumb: string | null;
  trashed_at: number | null;
}

// From note_snippets (worker.ts), so a plain list never reads note bodies.
const SNIPPET = "(SELECT s.snippet FROM note_snippets s WHERE s.id = n.id)";
// In search results the snippet starts a little before the first hit, like Evernote's.
const SNIPPET_AT_HIT = `CASE WHEN instr(lower(n.body_text), lower(?1)) > 40
  THEN '…' || substr(n.body_text, instr(lower(n.body_text), lower(?1)) - 30, 240) ELSE ${SNIPPET} END`;
const select = (snippet: string) => `SELECT n.id, n.title, ${snippet} AS snippet, n.updated, n.created, n.notebook_id, n.tag_ids, n.pinned_at, n.locked_at, n.trashed_at,
  (SELECT a.id FROM attachments a WHERE a.note_id = n.id AND a.deleted = 0 AND a.mime LIKE 'image/%' ORDER BY a.created LIMIT 1) AS thumb
  FROM notes n`;

/**
 * Evernote's note-list filters (Tags, Located in, Created, Updated, Contains).
 * They live in the URL next to the list context, so back/forward and fold/unfold keep them.
 */
export interface ListFilters {
  tags: string[]; // tag ids; a note must have all of them
  in: string | null; // notebook or stack id
  created: string | null; // DATE_PRESETS key or "YYYYMMDD..YYYYMMDD"
  updated: string | null;
  has: string[]; // CONTAINS keys
}

export const NO_FILTERS: ListFilters = { tags: [], in: null, created: null, updated: null, has: [] };

export const DATE_PRESETS: [string, string][] = [
  ["day", "Today"],
  ["day-1", "Since yesterday"],
  ["day-7", "Last 7 days"],
  ["day-30", "Last 30 days"],
  ["day-365", "Last 12 months"],
];

export const CONTAINS_OPTIONS: [string, string][] = [
  ["task", "Tasks"],
  ["checklist", "Checklists"],
  ["image", "Images"],
  ["pdf", "PDFs"],
  ["audio", "Audio"],
  ["document", "Documents"],
  ["table", "Tables"],
  ["url", "Links"],
  ["codeblock", "Code blocks"],
];

export function filtersFromSearch(search: string): ListFilters {
  const p = new URLSearchParams(search);
  return { tags: p.getAll("tag"), in: p.get("in"), created: p.get("created"), updated: p.get("updated"), has: p.getAll("has") };
}

export function writeFilters(p: URLSearchParams, f: ListFilters) {
  for (const k of ["tag", "in", "created", "updated", "has"]) p.delete(k);
  for (const t of f.tags) p.append("tag", t);
  if (f.in) p.set("in", f.in);
  if (f.created) p.set("created", f.created);
  if (f.updated) p.set("updated", f.updated);
  for (const h of f.has) p.append("has", h);
}

export function filterCount(f: ListFilters): number {
  return f.tags.length + f.has.length + (f.in ? 1 : 0) + (f.created ? 1 : 0) + (f.updated ? 1 : 0);
}

const DAY = 86_400_000;

function ymd(s: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  return m ? new Date(+m[1]!, +m[2]! - 1, +m[3]!).getTime() : null;
}

/** [from, to) in epoch ms for a date filter value. */
export function dateRange(spec: string, now = new Date()): [number | null, number | null] {
  const range = /^(\d{8})?\.\.(\d{8})?$/.exec(spec);
  if (range) {
    const to = range[2] ? ymd(range[2]) : null;
    return [range[1] ? ymd(range[1]) : null, to == null ? null : to + DAY];
  }
  return [parseDateSpec(spec, now), null];
}

export function dateLabel(spec: string): string {
  const preset = DATE_PRESETS.find(([k]) => k === spec);
  if (preset) return preset[1];
  const [from, to] = dateRange(spec);
  const f = (ms: number) => dateFmt({ year: "numeric", month: "short", day: "numeric" }).format(ms);
  if (from != null && to != null) return `${f(from)} – ${f(to - DAY)}`;
  if (from != null) return `Since ${f(from)}`;
  if (to != null) return `Before ${f(to)}`;
  return spec;
}

function filterWhere(f: ListFilters, where: string[], params: unknown[]) {
  for (const t of f.tags) {
    where.push("EXISTS (SELECT 1 FROM json_each(n.tag_ids) je WHERE je.value = ?)");
    params.push(t);
  }
  if (f.in) {
    where.push(`n.notebook_id IN ${SUBTREE_SQL}`);
    params.push(f.in);
  }
  for (const col of ["created", "updated"] as const) {
    const spec = f[col];
    if (!spec) continue;
    const [from, to] = dateRange(spec);
    if (from != null) {
      where.push(`n.${col} >= ?`);
      params.push(from);
    }
    if (to != null) {
      where.push(`n.${col} < ?`);
      params.push(to);
    }
  }
  for (const h of f.has) {
    if (!CONTAINS_OPTIONS.some(([k]) => k === h)) continue;
    where.push(compileQuery(`contains:${h}`, { flags: true }).where);
  }
}

/**
 * A note list as two queries: `idsSql` lists every matching note id in order (cheap: the count, select-all,
 * keyboard moves and ranges use it), `sql` the full rows, which callers LIMIT to the cards actually drawn.
 */
export function listQuery(
  ctx: ListContext,
  sort: SortKey,
  desc: boolean,
  filters: ListFilters = NO_FILTERS,
): { sql: string; params: unknown[]; idsSql: string; idsParams: unknown[]; terms: string[]; byIdsSql: string; byIdsParams: (idsJson: string) => unknown[] } {
  const where: string[] = ["n.deleted = 0"];
  const params: unknown[] = [];
  let terms: string[] = [];
  where.push(ctx.kind === "trash" ? "n.trashed_at IS NOT NULL" : "n.trashed_at IS NULL");
  switch (ctx.kind) {
    case "notebook":
      where.push("n.notebook_id = ?");
      params.push(ctx.id);
      break;
    case "stack":
      where.push(`n.notebook_id IN ${SUBTREE_SQL}`);
      params.push(ctx.id);
      break;
    case "tag":
      where.push("EXISTS (SELECT 1 FROM json_each(n.tag_ids) je WHERE je.value = ?)");
      params.push(ctx.id);
      break;
    case "shortcuts":
      where.push("n.id IN (SELECT target_id FROM shortcuts WHERE kind = 'note' AND deleted = 0)");
      break;
    case "search": {
      const c = compileQuery(ctx.q, { wordStart: true, flags: true });
      where.push(`(${c.where})`);
      params.push(...c.params);
      terms = c.terms;
      break;
    }
  }
  filterWhere(filters, where, params);
  const dir = desc ? "DESC" : "ASC";
  const order =
    sort === "title" ? `n.title COLLATE NOCASE ${dir}, n.updated DESC` : sort === "deleted" ? `n.trashed_at ${dir}` : `n.${sort} ${dir}`;
  // Pinned notes float to the top inside a notebook, like Evernote's "Pin to notebook".
  // Shortcuts keep the owner's order (the sidebar's, and Ctrl+1..9), like Evernote's Shortcuts list.
  const pin =
    ctx.kind === "notebook"
      ? "(n.pinned_at IS NULL), "
      : ctx.kind === "shortcuts"
        ? "(SELECT MIN(position) FROM shortcuts WHERE target_id = n.id AND kind = 'note' AND deleted = 0), "
        : "";
  // ?1 is the first search term (numbered so the WHERE clause's plain ? placeholders come after it).
  const head = terms[0] ? select(SNIPPET_AT_HIT) : select(SNIPPET);
  const tail = `WHERE ${where.join(" AND ")} ORDER BY ${pin}${order}`;
  return {
    sql: `${head} ${tail}`,
    params: terms[0] ? [terms[0], ...params] : params,
    idsSql: `SELECT n.id FROM notes n ${tail}`,
    idsParams: params,
    terms,
    // The rows for ids already found (a JSON array), in that order: a search's WHERE scans every note, so the
    // drawn rows are fetched by id instead of running it a second time.
    byIdsSql: `${head} JOIN json_each(?) j ON j.value = n.id ORDER BY j.key`,
    byIdsParams: (idsJson) => (terms[0] ? [terms[0], idsJson] : [idsJson]),
  };
}

export function contextFromPath(path: string, search: string): ListContext | null {
  const q = new URLSearchParams(search);
  const [, a, b] = path.split("/");
  switch (a) {
    case "notes":
      return { kind: "all" };
    case "notebook":
      return b ? { kind: "notebook", id: b } : null;
    case "stack":
      return b ? { kind: "stack", id: b } : null;
    case "tag":
      return b ? { kind: "tag", id: b } : null;
    case "search":
      return { kind: "search", q: q.get("q") ?? "" };
    case "trash":
      return { kind: "trash" };
    case "shortcuts":
      return { kind: "shortcuts" };
    default:
      return null;
  }
}
